import { Button, cn, V2Loader } from "web-design-system";
import { Loader2 } from "lucide-react";
import { useDebounceValue } from "usehooks-ts";
import type { MarketingResourceType } from "creso-core/types";
import { Filter, FilterList } from "@core/components/filter-list";
import { ArticleCard } from "./article-card";
import VideoCard from "./video-card";
import { getSelectedTopicIds } from "../hooks/use-resource-topic-filters";
import { useInfiniteMarketingResources } from "../hooks/use-infinite-marketing-resources";

const PAGE_SIZE = 10;

type KnowledgeNonNewsletterTab = "article" | "tutorial" | "guide";

const resourceTypeMap: Record<
  KnowledgeNonNewsletterTab,
  MarketingResourceType
> = {
  article: "article",
  tutorial: "tutorial",
  guide: "guide",
};

type KnowledgeNonNewsletterContentProps = {
  selectedType: KnowledgeNonNewsletterTab;
  search: string;
  selectedFilters: string[];
  clearFilters: () => void;
  filters: Filter[];
};

export function KnowledgeNonNewsletterContent({
  selectedType,
  search,
  selectedFilters,
  clearFilters,
  filters,
}: KnowledgeNonNewsletterContentProps) {
  const resourceType = resourceTypeMap[selectedType];
  const [debouncedSearch] = useDebounceValue(search, 300);

  const selectedTopicIds = getSelectedTopicIds(selectedFilters);
  const topicIdsKey = selectedTopicIds.join(",");
  const topicIds = selectedTopicIds.length > 0 ? selectedTopicIds : undefined;

  const {
    page,
    resourcesAccumulated,
    isFetching,
    isInitialLoading,
    scrollContainerRef,
    loadMoreSentinelRef,
  } = useInfiniteMarketingResources({
    resource_type: resourceType,
    search: debouncedSearch || undefined,
    topic_ids: topicIds,
    page_size: PAGE_SIZE,
    listResetKey: `${selectedType}|${debouncedSearch}|${topicIdsKey}`,
  });

  const effectiveResources = resourcesAccumulated;

  const showFilters = resourceType !== "guide";

  return (
    <div className="flex flex-row flex-1 overflow-hidden gap-4 h-full">
      <div className="hidden self-stretch lg:block">
        {showFilters ? (
          <FilterList
            showAccordion={false}
            filters={filters}
            onClearAll={clearFilters}
          />
        ) : null}
      </div>

      <V2Loader
        isLoading={isInitialLoading}
        className="flex flex-1 overflow-hidden"
      >
        <div ref={scrollContainerRef} className="flex-1 overflow-y-auto pr-1">
          {effectiveResources.length > 0 ? (
            <div
              className={cn(
                "grid grid-cols-1 gap-4",
                showFilters
                  ? "md:grid-cols-2 xl:grid-cols-3"
                  : "md:grid-cols-2 xl:grid-cols-4"
              )}
            >
              {effectiveResources.map((item) =>
                ["article", "guide"].includes(item.resource_type) ? (
                  <ArticleCard key={item.id} item={item} />
                ) : (
                  <VideoCard
                    key={item.id}
                    item={item}
                    content={
                      <div className="text-sm font-medium truncate px-1 py-2">
                        {item.title}
                      </div>
                    }
                  />
                )
              )}
            </div>
          ) : resourceType === "guide" ? (
            <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card px-6 text-center">
              <div className="text-sm font-medium">
                No knowledge resources match the current search.
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                Try a different keyword.
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card px-6 text-center">
              <div className="text-sm font-medium">
                No knowledge resources match the current search or filters.
              </div>
              <div className="mt-1 text-sm text-muted-foreground">
                Try a different keyword or clear the applied filters.
              </div>
              <Button variant="outline" className="mt-4" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          )}

          <div
            ref={loadMoreSentinelRef}
            className="flex items-center justify-center"
          >
            {page > 1 && isFetching ? (
              <Loader2
                className="size-5 animate-spin text-muted-foreground py-6"
                aria-hidden
              />
            ) : null}
          </div>
        </div>
      </V2Loader>
    </div>
  );
}
